import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import compact from 'lodash.compact';
import { ACTIVITIES_DATA } from '@amplify/instruction-groups';
import { buildRecommendationsText } from '../../../../utils/shared/instructionUtils';
import { TextCell } from '../../../../components/cell/shared/Cells';
import ObservationSection from '../observations/ObservationSection';
import './StudentRecommendations.scss';
import { generateReactKey } from '../../../../utils/shared/commonUtils';
import { STUD_NAME_GROUP_DES } from '../../../../constants/reading/DemoModeConstants';

const NO_GROUP_FOR_STUDENT_MESSAGE =
    'Please finish assessing this student to receive instructional recommendations.';

const StudentRecommendations = ({
    studentInfo,
    mainGroup,
    additionalGroup,
    groupDescription,
    observations,
    activities,
    observationsContent,
    measureList,
    observationActivitiesMapper,
    selectedGrade,
    staffId,
    lrsTrackingInfo,
    onHelpIconClick,
    selectObservation,
    sessionId,
    lastLocation,
    lrsGroup,
    isSecure,
}) => {
    const groups = compact(Array.of(mainGroup, additionalGroup));
    const getNoGroupsMessage = () =>
        !isSecure ? (
            <TextCell
                content={NO_GROUP_FOR_STUDENT_MESSAGE}
                classNames="no-groups text-center w-100"
                isDataCell={false}
            />
        ) : (
            <div className="w-100" />
        );

    return (
        <div className="instruction-content" data-tour-step={STUD_NAME_GROUP_DES}>
            <div className="student-recommendations">
                {mainGroup ? (
                    <div className="recommendations-section">
                        <div className="student-name-section">
                            {`${studentInfo.firstName} ${studentInfo.lastName}`}
                        </div>
                        <div className="recommendations-text">
                            {buildRecommendationsText(studentInfo.firstName, groupDescription)}
                        </div>
                    </div>
                ) : (
                    getNoGroupsMessage()
                )}
                {!isEmpty(groups) && (
                    <div className="groups-section">
                        <div className="group-title">Group(s):</div>
                        {groups.map((group, index) => (
                            <div key={generateReactKey(index)} className="group-name">
                                <i className="group-icon" />
                                {group}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {mainGroup && (
                <ObservationSection
                    content={observationsContent}
                    observations={observations}
                    staticActivitiesIds={activities}
                    withDescription
                    staffId={staffId}
                    studentId={studentInfo.id}
                    lrsTrackingInfo={lrsTrackingInfo}
                    activitiesData={ACTIVITIES_DATA}
                    firstName={studentInfo.firstName}
                    measureList={measureList}
                    observationActivitiesMapper={observationActivitiesMapper}
                    selectedGrade={selectedGrade}
                    onHelpIconClick={onHelpIconClick}
                    selectObservation={selectObservation}
                    sessionId={sessionId}
                    lastLocation={lastLocation}
                    lrsGroup={lrsGroup}
                />
            )}
        </div>
    );
};

StudentRecommendations.defaultProps = {
    studentInfo: {},
    mainGroup: null,
    additionalGroup: null,
    groupDescription: null,
    observations: [],
    activities: [],
    observationsContent: [],
    measureList: [],
    observationActivitiesMapper: {},
    selectedGrade: null,
    staffId: null,
    lrsTrackingInfo: {},
    onHelpIconClick: () => {},
    selectObservation: () => {},
    sessionId: '',
    lastLocation: {},
    lrsGroup: '',
    isSecure: false,
};

StudentRecommendations.propTypes = {
    studentInfo: PropTypes.object,
    mainGroup: PropTypes.string,
    additionalGroup: PropTypes.object,
    groupDescription: PropTypes.array,
    observations: PropTypes.array,
    activities: PropTypes.array,
    observationsContent: PropTypes.array,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    selectedGrade: PropTypes.string,
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    onHelpIconClick: PropTypes.func,
    selectObservation: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    lrsGroup: PropTypes.string,
    isSecure: PropTypes.bool,
};

export default StudentRecommendations;
